import { Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import Lottie from 'lottie-react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { AppointmentType, Route, RouteParameters } from '../../../constants'
import { LocaleHourCycleKey, useAppointment, useKeyBindings, useUserSettings } from '../../../hooks'
import { appointmentTypeToDisplayText, hydratePathname, isAwaitingProviderMatching } from '../../../util'
import { Spacer } from '../../Spacer'
import animationData from '../assets/lottie/success.json'
import { getCabinResource, getSpecialistResource } from '../util'
import { useAppointmentTypeTranslation } from '../util/useAppointmentTypeTranslation'

interface Props {
  isReschedule?: boolean
}

const Container = styled('div')`
  display: grid;
  place-items: center;
`

const toLongTimeZonedDateText = (date: Date, locale: string, timeZone: string) => {
  const formatter = Intl.DateTimeFormat(locale, {
    dateStyle: 'full',
    timeZone,
  })

  return formatter.format(date)
}

const toShortTimeZonedDateText = (date: Date, locale: string, timeZone: string, hourCycle: LocaleHourCycleKey) => {
  const formatter = Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
    timeZoneName: 'short',
    hourCycle,
  })

  return formatter.format(date)
}

const Confirmation = ({ isReschedule }: Props) => {
  const { locale, hourCycle } = useUserSettings()
  const { t } = useTranslation()
  const { appointment } = useAppointment()
  const navigate = useNavigate()

  const appointmentParams = {
    appointmentType: appointmentTypeToDisplayText[appointment?.type.appointment_type ?? AppointmentType.HEARING_AID_EVALUATION],
    fitterName: getSpecialistResource(appointment?.resources)?.name,
    cabinName: getCabinResource(appointment?.resources)?.name,
    shopId: appointment?.resources?.[0]?.shop_id,
  }

  const { getAppointmentTypeTranslation } = useAppointmentTypeTranslation(appointmentParams)

  const redirectToAction = useCallback(
    (route: string) => {
      const pathname = hydratePathname(route, {
        [RouteParameters.APPOINTMENT_ID]: appointment?.id as string,
        [RouteParameters.COUNTRY]: appointment?.country,
      })

      navigate(pathname)
    },
    [appointment?.id, appointment?.country, navigate]
  )

  useKeyBindings({
    'c a n': () => redirectToAction(Route.APPOINTMENT_CANCELLATION),
    'r e s': () => redirectToAction(Route.APPOINTMENT_RESCHEDULE),
  })

  if (!appointment) {
    return null
  }

  const [dateText, customerTime, resourceTime] = appointment
    ? [
        toLongTimeZonedDateText(appointment.start_date, locale, appointment.scheduled_customer_timezone as string),
        toShortTimeZonedDateText(appointment.start_date, locale, appointment.scheduled_customer_timezone as string, hourCycle),
        toShortTimeZonedDateText(
          appointment.start_date,
          locale,
          appointment.resources[0]?.timezone ?? (appointment.scheduled_customer_timezone as string),
          hourCycle
        ),
      ]
    : [' ', ' ', ' ']

  const areTimesTheSame = customerTime === resourceTime

  const dateAndTime = areTimesTheSame
    ? t('common.label.timeWithCustomerTimeSuffix', { customerTime, customerTimeText: t('common.label.customerTime') })
    : t('common.label.timeWithCustomerAndProviderTimeSuffix', {
        customerTime,
        customerTimeText: t('common.label.customerTime'),
        resourceTime,
        resourceTimeText: t('common.label.resourceTime'),
      })

  const title = isReschedule ? 'pages.appointment.reschedule.success.message' : 'pages.appointment.success.message'

  return (
    <Container>
      <Lottie animationData={animationData} loop={false} style={{ height: '16rem' }} />
      <Typography variant="h1" textAlign="center">
        {t(title)}
      </Typography>
      <Spacer count={0.5} />
      <Typography textAlign="center">{getAppointmentTypeTranslation()}</Typography>
      {isAwaitingProviderMatching(appointment) && <Typography textAlign="center">{t('schedule.oneOfOurProvidersWillSeeYou')}</Typography>}
      <Spacer count={0.5} />
      <Typography>{dateText}</Typography>
      <Typography>{dateAndTime}</Typography>
    </Container>
  )
}

export { Confirmation }
